import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ArrowDown,
    ArrowUp,
    Grid3x3,
    GalleryHorizontal,
    LayoutGrid,
    Loader2,
    PanelLeft,
    PanelRight,
    RotateCcw,
    Undo2,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSettingsApi } from '../../../common/hooks/use-settings-api.hook';
import { useAutosave } from '../../../common/hooks/use-autosave.hook';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { ShopperPricesSetting } from '../settings/components/ShopperPricesSetting';
import { AssistantChipsSetting } from '../settings/components/AssistantChipsSetting';
import { SegmentedChoice } from '../../../components/design-system/common/SegmentedChoice';
import {
    SettingsColorField,
    SettingsField,
    SettingsGroup,
    SettingsToggleRow,
} from '../../../components/design-system/common/SettingsPanel';
import { CanvasPortal } from '../../../pages/vendor/configurations/studio-canvas';
import {
    BarStyle,
    FEATURE_KEYS,
    FeatureKey,
    MOBILE_ACTIONS,
    MobileActionId,
    RoomWidgetId,
    RoomZone,
    VisualizerLayout,
    WidgetPlacement,
} from './layout.schema';
import { moveWidget, normalizeLayout, PANEL_WIDTH_MAX, PANEL_WIDTH_MIN, presetLayout, updateWidget, widgetPlacement } from './layout.normalize';
import { PresetPicker } from './PresetPicker';
import { BoardTarget, ZoneBoard } from './ZoneBoard';
import { WidgetInspector } from './WidgetInspector';
import { LayoutPreview, PreviewDevice } from './LayoutPreview';
import { PreviewScreen, useLayoutPreview } from './useLayoutPreview';

type Tab = 'room' | 'gallery' | 'features';

const BACKGROUNDS = ['#FFFFFF', '#F3F1EC', '#ECE9E4', '#E6E9EE', '#2A2B2E', '#16171A'];

const GROUP_IDS = {
    presets: 'layout-group-presets',
    arrangement: 'layout-group-arrangement',
    panel: 'layout-group-panel',
    bars: 'layout-group-bars',
    canvas: 'layout-group-canvas',
    galleryHeader: 'layout-group-gallery-header',
    galleryRooms: 'layout-group-gallery-rooms',
    galleryBrowse: 'layout-group-gallery-browse',
} as const;

/** A settings group that can be scrolled to and flashed when picked in the preview. */
function Group({ id, flash, children, ...rest }: { id: string; flash: string | null } & Parameters<typeof SettingsGroup>[0]) {
    return (
        <div id={id} className={cn('-mx-2 scroll-mt-4 rounded-lg px-2 py-1 transition-colors duration-700', flash === id && 'bg-blue-500/[0.07]')}>
            <SettingsGroup {...rest}>{children}</SettingsGroup>
        </div>
    );
}

/** Icon tiles for a handful of visual choices (panel side, gallery arrangement). */
function TileChoice<T extends string>({ value, options, onChange }: {
    value: T;
    options: { value: T; label: string; icon: ReactNode }[];
    onChange: (v: T) => void;
}) {
    return (
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        aria-pressed={active}
                        onClick={() => onChange(o.value)}
                        className={cn(
                            'flex flex-col items-center gap-1.5 rounded-lg border px-2 py-2.5 text-xs transition-colors hover:bg-muted/60',
                            active ? 'border-foreground text-foreground ring-1 ring-foreground' : 'text-muted-foreground',
                        )}
                    >
                        <span className="flex size-7 items-center justify-center rounded-md bg-muted [&_svg]:size-4">{o.icon}</span>
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Visualizer → Customization → Layout: arrange the shopper's visualizer.
 *
 * The draft lives here and is saved a moment after each change, like every
 * other section of the studio. The canvas on the right is the vendor's real
 * visualizer showing the draft; pieces can be dragged there directly, or on
 * the zone map here — both edit the same draft.
 */
export function LayoutEditor() {
    const { t } = useTranslation();
    // The assistant is granted by a super admin; a vendor without it has nothing to switch.
    const { hasFeature } = useVendorPlan();
    const featureKeys = FEATURE_KEYS.filter((key) => key !== 'assistant' || hasFeature('smartAssistant'));
    const settingsAPI = useSettingsApi();

    const [layout, setLayout] = useState<VisualizerLayout>(() => normalizeLayout(null));
    const [loaded, setLoaded] = useState(false);
    const [alias, setAlias] = useState<string | undefined>();
    const history = useRef<VisualizerLayout[]>([]);
    const [canUndo, setCanUndo] = useState(false);

    const [tab, setTab] = useState<Tab>('room');
    const [device, setDevice] = useState<PreviewDevice>('desktop');
    const [edit, setEdit] = useState(true);
    const [selectedWidget, setSelectedWidget] = useState<RoomWidgetId | null>(null);
    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const [flash, setFlash] = useState<string | null>(null);
    const screen: PreviewScreen = tab === 'gallery' ? 'gallery' : 'room';

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const config = await settingsAPI.findConfig();
                if (cancelled) return;
                setAlias(config?.alias);
                setLayout(normalizeLayout(config?.visualizerLayout ?? null));
            } catch (error) {
                console.error('Could not load the visualizer layout:', error);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useAutosave({
        data: layout,
        ready: loaded,
        save: () => settingsAPI.updateConfig({ visualizerLayout: layout }),
    });

    const layoutRef = useRef(layout);
    layoutRef.current = layout;
    const lastRecordedAt = useRef(0);

    /**
     * Every edit goes through here, so it can be undone. A burst of edits (a
     * slider dragged, a title typed) is one step back, not fifty.
     */
    const commit = useCallback((next: VisualizerLayout | ((prev: VisualizerLayout) => VisualizerLayout)) => {
        const prev = layoutRef.current;
        const value = typeof next === 'function' ? next(prev) : next;
        if (value === prev) return;
        const now = Date.now();
        if (now - lastRecordedAt.current > 700) {
            history.current = [...history.current.slice(-29), prev];
            setCanUndo(true);
        }
        lastRecordedAt.current = now;
        layoutRef.current = value;
        setLayout(value);
    }, []);

    const undo = () => {
        const prev = history.current.pop();
        if (!prev) return;
        lastRecordedAt.current = 0;
        layoutRef.current = prev;
        setLayout(prev);
        setCanUndo(history.current.length > 0);
    };

    const desk = layout.room.desktop;
    const setDesk = (patch: Partial<VisualizerLayout['room']['desktop']>) =>
        commit((l) => ({ ...l, room: { ...l.room, desktop: { ...l.room.desktop, ...patch } } }));
    const setPanel = (patch: Partial<VisualizerLayout['room']['desktop']['panel']>) =>
        commit((l) => ({ ...l, room: { ...l.room, desktop: { ...l.room.desktop, panel: { ...l.room.desktop.panel, ...patch } } } }));
    const setMobile = (patch: Partial<VisualizerLayout['room']['mobile']>) =>
        commit((l) => ({ ...l, room: { ...l.room, mobile: { ...l.room.mobile, ...patch } } }));
    const setGallery = (patch: Partial<VisualizerLayout['gallery']>) =>
        commit((l) => ({ ...l, gallery: { ...l.gallery, ...patch } }));
    const setHeader = (patch: Partial<VisualizerLayout['gallery']['header']>) =>
        commit((l) => ({ ...l, gallery: { ...l.gallery, header: { ...l.gallery.header, ...patch } } }));
    const setFeature = (key: FeatureKey, on: boolean) =>
        commit((l) => ({ ...l, features: { ...l.features, [key]: on } }));

    const moveTo = useCallback((id: RoomWidgetId, target: BoardTarget, beforeId: RoomWidgetId | null) => {
        commit((l) => (target === 'hidden' ? updateWidget(l, id, { visible: false }) : moveWidget(l, id, target, beforeId)));
        setSelectedWidget(id);
    }, [commit]);

    const reveal = (groupId: string) => {
        setFlash(groupId);
        window.setTimeout(() => setFlash((f) => (f === groupId ? null : f)), 1400);
        window.requestAnimationFrame(() => {
            document.getElementById(groupId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    // Something was clicked on the preview: open what edits it.
    const handlePreviewSelect = useCallback((key: string) => {
        if (key.startsWith('widget:')) {
            setTab('room');
            setDevice('desktop');
            setSelectedWidget(key.slice(7) as RoomWidgetId);
            setSelectedRegion(null);
            reveal(GROUP_IDS.arrangement);
            return;
        }
        setSelectedWidget(null);
        setSelectedRegion(key);
        const map: Record<string, [Tab, string]> = {
            panel: ['room', GROUP_IDS.panel],
            topBar: ['room', GROUP_IDS.bars],
            surfaceTools: ['room', GROUP_IDS.bars],
            'gallery.header': ['gallery', GROUP_IDS.galleryHeader],
            'gallery.filters': ['gallery', GROUP_IDS.galleryBrowse],
            'gallery.grid': ['gallery', GROUP_IDS.galleryRooms],
        };
        const target = map[key];
        if (target) {
            setTab(target[0]);
            reveal(target[1]);
        }
    }, []);

    const preview = useLayoutPreview({
        alias,
        layout,
        screen,
        edit,
        selected: selectedWidget ? `widget:${selectedWidget}` : selectedRegion,
        onSelect: handlePreviewSelect,
        onMove: (id, zone, beforeId) => moveTo(id, zone, beforeId),
        onPanelWidth: (width) => setPanel({ width: Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, width)) }),
    });

    const selectedPlacement = selectedWidget ? widgetPlacement(layout, selectedWidget) : undefined;

    const barStyles = useMemo(() => ([
        { value: 'attached' as BarStyle, label: t('VISUALIZER_LAYOUT.BAR_STYLE.ATTACHED'), hint: t('VISUALIZER_LAYOUT.BAR_STYLE.ATTACHED_HINT') },
        { value: 'floating' as BarStyle, label: t('VISUALIZER_LAYOUT.BAR_STYLE.FLOATING'), hint: t('VISUALIZER_LAYOUT.BAR_STYLE.FLOATING_HINT') },
        { value: 'glass' as BarStyle, label: t('VISUALIZER_LAYOUT.BAR_STYLE.GLASS'), hint: t('VISUALIZER_LAYOUT.BAR_STYLE.GLASS_HINT') },
    ]), [t]);

    if (!loaded) {
        return (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
            </div>
        );
    }

    // ── Room · desktop ─────────────────────────────────────────────────────
    const roomDesktop = (
        <>
            <Group id={GROUP_IDS.presets} flash={flash} title={t('VISUALIZER_LAYOUT.PRESETS.TITLE')} description={t('VISUALIZER_LAYOUT.PRESETS.DESC')}>
                <PresetPicker
                    layout={layout}
                    onPick={(id) => { commit(presetLayout(id)); setSelectedWidget(null); }}
                />
            </Group>

            <Group id={GROUP_IDS.arrangement} flash={flash} title={t('VISUALIZER_LAYOUT.ARRANGEMENT.TITLE')} description={t('VISUALIZER_LAYOUT.ARRANGEMENT.DESC')}>
                <ZoneBoard
                    layout={layout}
                    selected={selectedWidget}
                    onSelect={(id) => { setSelectedWidget(id); setSelectedRegion(null); }}
                    onMove={moveTo}
                />
                {selectedPlacement && (
                    <WidgetInspector
                        placement={selectedPlacement}
                        onChange={(patch: Partial<WidgetPlacement>) => commit((l) => updateWidget(l, selectedPlacement.id, patch))}
                        onMove={(zone: RoomZone) => moveTo(selectedPlacement.id, zone, null)}
                        onClose={() => setSelectedWidget(null)}
                    />
                )}
            </Group>

            <Group id={GROUP_IDS.panel} flash={flash} title={t('VISUALIZER_LAYOUT.PANEL.TITLE')} description={t('VISUALIZER_LAYOUT.PANEL.DESC')}>
                <SettingsField label={t('VISUALIZER_LAYOUT.PANEL.SIDE')}>
                    <TileChoice
                        value={desk.panel.side}
                        onChange={(side) => setPanel({ side })}
                        options={[
                            { value: 'left', label: t('VISUALIZER_LAYOUT.PANEL.LEFT'), icon: <PanelLeft /> },
                            { value: 'right', label: t('VISUALIZER_LAYOUT.PANEL.RIGHT'), icon: <PanelRight /> },
                        ]}
                    />
                </SettingsField>
                <SettingsField label={t('VISUALIZER_LAYOUT.PANEL.WIDTH')} hint={t('VISUALIZER_LAYOUT.PANEL.WIDTH_HINT')}>
                    <div className="flex items-center gap-3">
                        <Slider
                            min={PANEL_WIDTH_MIN}
                            max={PANEL_WIDTH_MAX}
                            step={10}
                            value={[desk.panel.width]}
                            onValueChange={(v) => setPanel({ width: v[0] })}
                            className="flex-1"
                            aria-label={t('VISUALIZER_LAYOUT.PANEL.WIDTH')}
                        />
                        <span className="min-w-12 text-end text-sm tabular-nums text-muted-foreground">{desk.panel.width}px</span>
                    </div>
                </SettingsField>
                <SettingsField label={t('VISUALIZER_LAYOUT.PANEL.STYLE')}>
                    <SegmentedChoice
                        value={desk.panel.style}
                        onChange={(style) => setPanel({ style })}
                        options={[
                            { value: 'attached', label: t('VISUALIZER_LAYOUT.PANEL.ATTACHED'), hint: t('VISUALIZER_LAYOUT.PANEL.ATTACHED_HINT') },
                            { value: 'floating', label: t('VISUALIZER_LAYOUT.PANEL.FLOATING'), hint: t('VISUALIZER_LAYOUT.PANEL.FLOATING_HINT') },
                        ]}
                    />
                </SettingsField>
                <SettingsField label={t('VISUALIZER_LAYOUT.PANEL.CATALOG_COLUMNS')}>
                    <SegmentedChoice
                        value={desk.panel.catalogColumns}
                        onChange={(catalogColumns) => setPanel({ catalogColumns })}
                        options={[1, 2, 3, 4].map((n) => ({ value: n, label: String(n) }))}
                    />
                </SettingsField>
                <SettingsToggleRow
                    id="layout-panel-open"
                    label={t('VISUALIZER_LAYOUT.PANEL.START_OPEN')}
                    description={t('VISUALIZER_LAYOUT.PANEL.START_OPEN_HINT')}
                    checked={desk.panel.start === 'open'}
                    onCheckedChange={(on) => setPanel({ start: on ? 'open' : 'auto' })}
                />
                <SettingsToggleRow
                    id="layout-panel-collapsible"
                    label={t('VISUALIZER_LAYOUT.PANEL.COLLAPSIBLE')}
                    description={t('VISUALIZER_LAYOUT.PANEL.COLLAPSIBLE_HINT')}
                    checked={desk.panel.collapsible}
                    onCheckedChange={(collapsible) => setPanel({ collapsible })}
                />
                <SettingsToggleRow
                    id="layout-panel-cart-tab"
                    label={t('VISUALIZER_LAYOUT.PANEL.CART_TAB')}
                    checked={desk.panel.tabs.cart}
                    onCheckedChange={(cart) => setPanel({ tabs: { ...desk.panel.tabs, cart } })}
                />
                <SettingsToggleRow
                    id="layout-panel-favorites-tab"
                    label={t('VISUALIZER_LAYOUT.PANEL.FAVORITES_TAB')}
                    checked={desk.panel.tabs.favorites}
                    onCheckedChange={(favorites) => setPanel({ tabs: { ...desk.panel.tabs, favorites } })}
                />
            </Group>

            <Group id={GROUP_IDS.bars} flash={flash} title={t('VISUALIZER_LAYOUT.BARS.TITLE')} description={t('VISUALIZER_LAYOUT.BARS.DESC')}>
                <SettingsField label={t('VISUALIZER_LAYOUT.BARS.TOP_BAR')} hint={t('VISUALIZER_LAYOUT.BARS.TOP_BAR_HINT')}>
                    <SegmentedChoice value={desk.topBar.style} onChange={(style) => setDesk({ topBar: { style } })} options={barStyles} />
                </SettingsField>
                <SettingsField label={t('VISUALIZER_LAYOUT.BARS.TOOLS')} hint={t('VISUALIZER_LAYOUT.BARS.TOOLS_HINT')}>
                    <SegmentedChoice value={desk.surfaceTools.style} onChange={(style) => setDesk({ surfaceTools: { style } })} options={barStyles} />
                </SettingsField>
            </Group>

            <Group id={GROUP_IDS.canvas} flash={flash} title={t('VISUALIZER_LAYOUT.CANVAS.TITLE')} description={t('VISUALIZER_LAYOUT.CANVAS.DESC')}>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setDesk({ canvas: {} })}
                        aria-pressed={!desk.canvas.background}
                        className={cn(
                            'h-8 rounded-md border px-2.5 text-xs transition-colors hover:bg-muted/60',
                            !desk.canvas.background ? 'border-foreground text-foreground ring-1 ring-foreground' : 'text-muted-foreground',
                        )}
                    >
                        {t('VISUALIZER_LAYOUT.CANVAS.DEFAULT')}
                    </button>
                    {BACKGROUNDS.map((c) => (
                        <button
                            key={c}
                            type="button"
                            aria-label={c}
                            aria-pressed={desk.canvas.background === c}
                            onClick={() => setDesk({ canvas: { background: c } })}
                            className={cn(
                                'size-8 rounded-md border transition-transform hover:scale-105',
                                desk.canvas.background === c && 'ring-2 ring-foreground ring-offset-2 ring-offset-background',
                            )}
                            style={{ backgroundColor: c }}
                        />
                    ))}
                </div>
                <SettingsColorField
                    id="layout-canvas-color"
                    label={t('VISUALIZER_LAYOUT.CANVAS.CUSTOM')}
                    value={desk.canvas.background ?? ''}
                    auto="#EBEBEB"
                    autoLabel={t('VISUALIZER_LAYOUT.CANVAS.DEFAULT')}
                    onChange={(v) => setDesk({ canvas: v ? { background: v } : {} })}
                />
            </Group>
        </>
    );

    // ── Room · mobile ──────────────────────────────────────────────────────
    const mobile = layout.room.mobile;
    const mobileOrder: MobileActionId[] = [...mobile.actions, ...MOBILE_ACTIONS.filter((a) => !mobile.actions.includes(a))];
    const moveAction = (a: MobileActionId, dir: -1 | 1) => {
        const list = [...mobile.actions];
        const i = list.indexOf(a);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= list.length) return;
        [list[i], list[j]] = [list[j], list[i]];
        setMobile({ actions: list });
    };
    const roomMobile = (
        <>
            <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">{t('VISUALIZER_LAYOUT.MOBILE.NOTE')}</p>
            <SettingsGroup title={t('VISUALIZER_LAYOUT.MOBILE.ACTIONS')} description={t('VISUALIZER_LAYOUT.MOBILE.ACTIONS_DESC')}>
                <ul className="flex flex-col divide-y rounded-md border">
                    {mobileOrder.map((a) => {
                        const on = mobile.actions.includes(a);
                        const index = mobile.actions.indexOf(a);
                        return (
                            <li key={a} className={cn('flex items-center gap-3 px-3 py-2', !on && 'bg-muted/40')}>
                                <Switch
                                    id={`layout-mobile-${a}`}
                                    checked={on}
                                    onCheckedChange={(v) => setMobile({ actions: v ? [...mobile.actions, a] : mobile.actions.filter((x) => x !== a) })}
                                />
                                <Label htmlFor={`layout-mobile-${a}`} className={cn('flex-1 text-sm font-normal', on ? 'text-foreground' : 'text-muted-foreground')}>
                                    {t(`VISUALIZER_LAYOUT.MOBILE.ACTION.${a.toUpperCase()}`)}
                                </Label>
                                {on && (
                                    <span className="flex items-center gap-0.5">
                                        <Button variant="ghost" size="icon-xs" disabled={index === 0} onClick={() => moveAction(a, -1)} aria-label={t('VISUALIZER_LAYOUT.INSPECTOR.MOVE_UP')}>
                                            <ArrowUp className="size-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="icon-xs" disabled={index === mobile.actions.length - 1} onClick={() => moveAction(a, 1)} aria-label={t('VISUALIZER_LAYOUT.INSPECTOR.MOVE_DOWN')}>
                                            <ArrowDown className="size-3.5" />
                                        </Button>
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
                <SettingsField label={t('VISUALIZER_LAYOUT.MOBILE.MAX_VISIBLE')} hint={t('VISUALIZER_LAYOUT.MOBILE.MAX_VISIBLE_HINT')}>
                    <SegmentedChoice
                        value={mobile.maxVisible}
                        onChange={(maxVisible) => setMobile({ maxVisible })}
                        options={[1, 2, 3, 4].map((n) => ({ value: n, label: String(n) }))}
                    />
                </SettingsField>
                <SettingsToggleRow
                    id="layout-mobile-change-room"
                    label={t('VISUALIZER_LAYOUT.MOBILE.CHANGE_ROOM')}
                    checked={mobile.showChangeRoom}
                    onCheckedChange={(showChangeRoom) => setMobile({ showChangeRoom })}
                />
            </SettingsGroup>
        </>
    );

    // ── Gallery ────────────────────────────────────────────────────────────
    const g = layout.gallery;
    const gallery = (
        <>
            <Group id={GROUP_IDS.galleryHeader} flash={flash} title={t('VISUALIZER_LAYOUT.GALLERY.HEADER')} description={t('VISUALIZER_LAYOUT.GALLERY.HEADER_DESC')}>
                <SettingsToggleRow id="layout-gallery-header" label={t('VISUALIZER_LAYOUT.GALLERY.SHOW_HEADER')} checked={g.header.visible} onCheckedChange={(visible) => setHeader({ visible })}>
                    <SettingsField label={t('VISUALIZER_LAYOUT.GALLERY.ALIGN')}>
                        <SegmentedChoice
                            value={g.header.align}
                            onChange={(align) => setHeader({ align })}
                            options={[
                                { value: 'split', label: t('VISUALIZER_LAYOUT.GALLERY.ALIGN_SPLIT') },
                                { value: 'center', label: t('VISUALIZER_LAYOUT.GALLERY.ALIGN_CENTER') },
                                { value: 'left', label: t('VISUALIZER_LAYOUT.GALLERY.ALIGN_LEFT') },
                            ]}
                        />
                    </SettingsField>
                    <SettingsField label={t('VISUALIZER_LAYOUT.GALLERY.TITLE')} htmlFor="layout-gallery-title" hint={t('VISUALIZER_LAYOUT.GALLERY.TITLE_HINT')}>
                        <Input id="layout-gallery-title" value={g.header.title} maxLength={120} placeholder={t('VISUALIZER_LAYOUT.GALLERY.TITLE_PLACEHOLDER')} onChange={(e) => setHeader({ title: e.target.value })} />
                    </SettingsField>
                    <SettingsField label={t('VISUALIZER_LAYOUT.GALLERY.SUBTITLE')} htmlFor="layout-gallery-subtitle">
                        <Input id="layout-gallery-subtitle" value={g.header.subtitle} maxLength={200} placeholder={t('VISUALIZER_LAYOUT.GALLERY.SUBTITLE_PLACEHOLDER')} disabled={!g.header.showSubtitle} onChange={(e) => setHeader({ subtitle: e.target.value })} />
                    </SettingsField>
                    <SettingsToggleRow id="layout-gallery-subtitle-on" label={t('VISUALIZER_LAYOUT.GALLERY.SHOW_SUBTITLE')} checked={g.header.showSubtitle} onCheckedChange={(showSubtitle) => setHeader({ showSubtitle })} />
                    <SettingsToggleRow id="layout-gallery-logo" label={t('VISUALIZER_LAYOUT.GALLERY.SHOW_LOGO')} checked={g.header.showLogo} onCheckedChange={(showLogo) => setHeader({ showLogo })} />
                </SettingsToggleRow>
            </Group>

            <Group id={GROUP_IDS.galleryRooms} flash={flash} title={t('VISUALIZER_LAYOUT.GALLERY.ROOMS')} description={t('VISUALIZER_LAYOUT.GALLERY.ROOMS_DESC')}>
                <SettingsField label={t('VISUALIZER_LAYOUT.GALLERY.ARRANGEMENT')}>
                    <TileChoice
                        value={g.arrangement}
                        onChange={(arrangement) => setGallery({ arrangement })}
                        options={[
                            { value: 'masonry', label: t('VISUALIZER_LAYOUT.GALLERY.MASONRY'), icon: <LayoutGrid /> },
                            { value: 'grid', label: t('VISUALIZER_LAYOUT.GALLERY.GRID'), icon: <Grid3x3 /> },
                            { value: 'carousel', label: t('VISUALIZER_LAYOUT.GALLERY.CAROUSEL'), icon: <GalleryHorizontal /> },
                        ]}
                    />
                </SettingsField>
                <SettingsField label={t(g.arrangement === 'carousel' ? 'VISUALIZER_LAYOUT.GALLERY.PER_VIEW' : 'VISUALIZER_LAYOUT.GALLERY.COLUMNS')}>
                    <div className="flex items-center gap-3">
                        <Slider min={2} max={6} step={1} value={[g.columns]} onValueChange={(v) => setGallery({ columns: v[0] })} className="flex-1" aria-label={t('VISUALIZER_LAYOUT.GALLERY.COLUMNS')} />
                        <span className="min-w-6 text-end text-sm tabular-nums text-muted-foreground">{g.columns}</span>
                    </div>
                </SettingsField>
                {g.arrangement !== 'masonry' && (
                    <SettingsField label={t('VISUALIZER_LAYOUT.GALLERY.CARD_SHAPE')}>
                        <SegmentedChoice
                            value={g.cardAspect}
                            onChange={(cardAspect) => setGallery({ cardAspect })}
                            options={[
                                { value: '4/3', label: '4:3' },
                                { value: '1/1', label: '1:1' },
                                { value: '3/4', label: '3:4' },
                                { value: '16/9', label: '16:9' },
                            ]}
                        />
                    </SettingsField>
                )}
                <SettingsToggleRow id="layout-gallery-names" label={t('VISUALIZER_LAYOUT.GALLERY.SHOW_NAMES')} description={t('VISUALIZER_LAYOUT.GALLERY.SHOW_NAMES_HINT')} checked={g.showNames} onCheckedChange={(showNames) => setGallery({ showNames })} />
                <SettingsField label={t('VISUALIZER_LAYOUT.GALLERY.WIDTH')}>
                    <SegmentedChoice
                        value={g.width}
                        onChange={(width) => setGallery({ width })}
                        options={[
                            { value: 'narrow', label: t('VISUALIZER_LAYOUT.GALLERY.NARROW') },
                            { value: 'normal', label: t('VISUALIZER_LAYOUT.GALLERY.NORMAL') },
                            { value: 'wide', label: t('VISUALIZER_LAYOUT.GALLERY.WIDE') },
                        ]}
                    />
                </SettingsField>
                <SettingsColorField
                    id="layout-gallery-bg"
                    label={t('VISUALIZER_LAYOUT.GALLERY.BACKGROUND')}
                    value={g.background ?? ''}
                    auto="#FFFFFF"
                    autoLabel={t('VISUALIZER_LAYOUT.CANVAS.DEFAULT')}
                    onChange={(v) => setGallery({ background: v || undefined })}
                />
            </Group>

            <Group id={GROUP_IDS.galleryBrowse} flash={flash} title={t('VISUALIZER_LAYOUT.GALLERY.BROWSE')} description={t('VISUALIZER_LAYOUT.GALLERY.BROWSE_DESC')}>
                <SettingsToggleRow id="layout-gallery-filters" label={t('VISUALIZER_LAYOUT.GALLERY.FILTERS')} description={t('VISUALIZER_LAYOUT.GALLERY.FILTERS_HINT')} checked={g.filters.visible} onCheckedChange={(visible) => setGallery({ filters: { visible } })} />
                <SettingsToggleRow id="layout-gallery-upload" label={t('VISUALIZER_LAYOUT.GALLERY.UPLOAD')} description={t('VISUALIZER_LAYOUT.GALLERY.UPLOAD_HINT')} checked={g.upload.visible} onCheckedChange={(visible) => setGallery({ upload: { ...g.upload, visible } })}>
                    <SettingsField label={t('VISUALIZER_LAYOUT.GALLERY.UPLOAD_POSITION')}>
                        <SegmentedChoice
                            value={g.upload.position}
                            onChange={(position) => setGallery({ upload: { ...g.upload, position } })}
                            options={[
                                { value: 'first', label: t('VISUALIZER_LAYOUT.GALLERY.FIRST') },
                                { value: 'last', label: t('VISUALIZER_LAYOUT.GALLERY.LAST') },
                            ]}
                        />
                    </SettingsField>
                    <SettingsToggleRow id="layout-gallery-qr" label={t('VISUALIZER_LAYOUT.GALLERY.QR')} description={t('VISUALIZER_LAYOUT.GALLERY.QR_HINT')} checked={g.upload.qr} onCheckedChange={(qr) => setGallery({ upload: { ...g.upload, qr } })} />
                </SettingsToggleRow>
            </Group>
        </>
    );

    // ── Features ───────────────────────────────────────────────────────────
    const features = (
        <SettingsGroup title={t('VISUALIZER_LAYOUT.FEATURES.TITLE')} description={t('VISUALIZER_LAYOUT.FEATURES.DESC')}>
            {featureKeys.map((key) => (
                <SettingsToggleRow
                    key={key}
                    id={`layout-feature-${key}`}
                    label={t(`VISUALIZER_LAYOUT.FEATURES.${key.toUpperCase()}`)}
                    description={t(`VISUALIZER_LAYOUT.FEATURES.${key.toUpperCase()}_HINT`)}
                    checked={layout.features[key]}
                    onCheckedChange={(on) => setFeature(key, on)}
                >
                    {/* What the assistant shows: prices or not, and the searches it suggests. */}
                    {key === 'assistant' && (
                        <>
                            <ShopperPricesSetting />
                            <AssistantChipsSetting />
                        </>
                    )}
                </SettingsToggleRow>
            ))}
            <p className="text-xs text-muted-foreground">{t('VISUALIZER_LAYOUT.FEATURES.WIDGETS_NOTE')}</p>
        </SettingsGroup>
    );

    return (
        <div className="flex flex-col gap-7">
            <div className="flex items-center justify-between gap-2">
                <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
                    <TabsList>
                        <TabsTrigger value="room">{t('VISUALIZER_LAYOUT.TAB.ROOM')}</TabsTrigger>
                        <TabsTrigger value="gallery">{t('VISUALIZER_LAYOUT.TAB.GALLERY')}</TabsTrigger>
                        <TabsTrigger value="features">{t('VISUALIZER_LAYOUT.TAB.FEATURES')}</TabsTrigger>
                    </TabsList>
                </Tabs>
                <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={undo} disabled={!canUndo}>
                        <Undo2 className="size-3.5" />
                        {t('VISUALIZER_LAYOUT.UNDO')}
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { commit(presetLayout('classic')); setSelectedWidget(null); }}
                    >
                        <RotateCcw className="size-3.5" />
                        {t('VISUALIZER_LAYOUT.RESET')}
                    </Button>
                </div>
            </div>

            {tab === 'room' && (
                <>
                    <SegmentedChoice
                        value={device}
                        onChange={setDevice}
                        options={[
                            { value: 'desktop' as PreviewDevice, label: t('VISUALIZER_LAYOUT.DEVICE.DESKTOP') },
                            { value: 'mobile' as PreviewDevice, label: t('VISUALIZER_LAYOUT.DEVICE.MOBILE') },
                        ]}
                    />
                    {device === 'desktop' ? roomDesktop : roomMobile}
                </>
            )}
            {tab === 'gallery' && gallery}
            {tab === 'features' && features}

            <CanvasPortal>
                <LayoutPreview
                    src={preview.src}
                    iframeRef={preview.iframeRef}
                    reloadKey={preview.reloadKey}
                    ready={preview.ready}
                    device={device}
                    onDeviceChange={setDevice}
                    screen={screen}
                    onScreenChange={(s) => setTab(s === 'gallery' ? 'gallery' : 'room')}
                    edit={edit}
                    onEditChange={setEdit}
                    onReload={preview.reload}
                />
            </CanvasPortal>
        </div>
    );
}

export default LayoutEditor;
